import type { AnyActorRef } from 'xstate';
import { assign, fromPromise, setup } from 'xstate';
import type { ServiceError } from '~/features/alerts/errors';
import { createErrorAssigner, handleServiceError } from '~/features/alerts/errors';
import { apiAxiosV1, deleteData, getData, patchData, setPathParams } from '~/utils/service';
import { ParseWorkflowType, resolveParseWorkflow } from './parseWorkflow';
import {
  AIParseJob,
  DocumentInfoValues,
  DocumentParseResp,
  DocumentReference,
  DocumentWithAiJobs,
  WSAIParseJobData,
  WSAiJobUpdateData,
} from './types';
import { isPlaceholderAiJob, trimFileName } from './utils';

const ROUTE_NAME = 'documents';
const DOCUMENT_DOWNLOAD_ROUTES = {
  get: '/documents/{id}/content',
};

const getHeaderValue = (headers: unknown, name: string): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;

  const maybeGetter = headers as { get?: (key: string) => unknown };
  const getterValue = maybeGetter.get?.(name) ?? maybeGetter.get?.(name.toLowerCase());
  if (typeof getterValue === 'string' && getterValue.trim()) return getterValue.trim();

  const entries = headers as Record<string, unknown>;
  const directValue = entries[name] ?? entries[name.toLowerCase()];
  return typeof directValue === 'string' && directValue.trim() ? directValue.trim() : undefined;
};

export const buildDownloadFilename = ({
  fallbackName,
  headers,
}: {
  fallbackName?: string;
  headers?: unknown;
}): string => {
  const baseName = fallbackName || getHeaderValue(headers, 'X-Document-Name') || 'document';
  const ext = getHeaderValue(headers, 'X-Document-Ext')?.replace(/^\./, '');

  if (!ext || baseName.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
    return baseName;
  }

  return `${baseName}.${ext}`;
};

// Error mapping for documents service
const documentsErrorMap = {
  400: { code: 'VALIDATION_ERROR', message: 'Invalid document data' },
  401: { code: 'UNAUTHORIZED', message: 'Please log in to manage documents' },
  403: { code: 'FORBIDDEN', message: 'You do not have permission to access this document' },
  404: { code: 'NOT_FOUND', message: 'Document not found' },
  413: {
    code: 'FILE_TOO_LARGE',
    message: 'Document is too large. Files must be 20MB or smaller.',
  },
  415: { code: 'INVALID_FILE_TYPE', message: 'Invalid file type' },
  500: { code: 'SERVER_ERROR', message: 'Server error occurred while processing documents' },
};

export const resolveDocumentUploadMetadata = (
  file: Pick<File, 'name'>,
  values?: DocumentInfoValues | null
) => {
  // [MCV-1765] A File Type is never guessed for a form upload (the modal requires one). Only a
  // drop, which has no form, files its document as 'other'.
  if (values && !values.docType?.value) {
    throw new Error('File Type is required for upload');
  }
  return {
    docCategory: values?.docCategory?.value || 'other',
    docDesc: values?.docDesc || ' ',
    // doc_name is VARCHAR(64): a longer name used to fail the upload with a 500.
    docName: trimFileName(values?.docName || file.name || ''),
    docType: values?.docType?.value ?? 'other',
  };
};

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

/**
 * [MCV-1800] How long the Documents page's batch keeps ownership of the API's "Document Upload
 * Complete" notification after a file is stored. That notification is sent when the upload
 * service finishes encrypting, after the POST that the batch waits on has already returned.
 */
export const OWNED_UPLOAD_NOTICE_TTL_MS = 3 * 60 * 1000;

/** A document whose upload-complete notification the Documents page's batch has covered. */
export type OwnedUploadNotice = { id: number; expiresAt: number };

/** The ownership marks that have not run out. */
export const liveOwnedUploadNotices = (notices: OwnedUploadNotice[], now = Date.now()) =>
  notices.filter(({ expiresAt }) => expiresAt > now);

export const documentsService = setup({
  types: {
    context: {} as {
      // Business data (cache)
      providerId: number | null;
      documentReferences: DocumentWithAiJobs[];
      // Cache management
      cacheTimestamp: number;
      cacheTTL: number;
      // Operation state
      loading: boolean;
      error: ServiceError | null;
      // Pending operation data (for confirmation flow)
      pendingDeleteId: number | null;
      downloadingDocumentId: number | null;
      // Upload operation data
      uploadingProviderId: number | null;
      uploadingFile: File | null;
      // Several files being combined into one packet (UPLOAD_COMBINED_DOCUMENT).
      uploadingFiles: File[] | null;
      uploadingValues: DocumentInfoValues | null;
      // AI parsing state
      parsingDocumentId: number | null;
      // [MCV-1800] One mark per stored-without-parsing document of the Documents page's batch
      // whose "Document Upload Complete" notification has not arrived yet. The batch has already
      // summarised that document, so the websocket router drops the notification that names its
      // id and takes the mark away.
      ownedUploadNotices: OwnedUploadNotice[];
      // Reference to view actor for sending events back to view
      viewRef: AnyActorRef | null;
    },
    events: {} as
      | { type: 'GET_DOCUMENTS'; providerId: number; forceRefresh?: boolean; viewRef?: AnyActorRef }
      | {
          type: 'UPLOAD_DOCUMENT';
          providerId: number;
          file: File;
          viewRef: AnyActorRef;
          values?: DocumentInfoValues;
        }
      | {
          type: 'UPLOAD_COMBINED_DOCUMENT';
          providerId: number;
          // Merged, in this order, into ONE packet PDF by POST /documents/combine.
          files: File[];
          viewRef: AnyActorRef;
          values?: DocumentInfoValues;
        }
      | { type: 'UPDATE_DOCUMENT'; id: number; values: DocumentInfoValues }
      | { type: 'DELETE_DOCUMENT'; id: number }
      | { type: 'AI_PARSE_DOCUMENT'; id: number }
      | { type: 'ON_JOB_UPDATE'; payload: WSAIParseJobData | WSAiJobUpdateData }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WebSocket notification payloads vary
      | { type: 'ON_NOTIFICATION'; payload: any }
      | { type: 'CONFIRM_DELETE' }
      | { type: 'CANCEL_DELETE' }
      | { type: 'DOWNLOAD_DOCUMENT'; id: number }
      | { type: 'CLEAR_ERROR' }
      | { type: 'ON_UPLOAD_NOTICE_OWNED'; documentId: number }
      // Without a documentId (an API that does not name the document) the oldest mark goes.
      | { type: 'ON_UPLOAD_NOTICE_CLAIMED'; documentId?: number },
    input: {} as Record<string, never>,
  },
  actors: {
    getDocuments: fromPromise(async ({ input }: { input: { providerId: number } }) => {
      // Fetch all documents without pagination - let ag-grid handle client-side pagination
      return await getData<DocumentWithAiJobs[]>(ROUTE_NAME, {
        params: { providerId: input.providerId },
      });
    }),
    uploadDocument: fromPromise(
      async ({
        input,
      }: {
        input: {
          providerId: number | null;
          file: File | null;
          files: File[] | null;
          values: DocumentInfoValues | null;
        };
      }) => {
        const combined = input.files && input.files.length > 1 ? input.files : null;
        const first = combined?.[0] ?? input.file;
        if (!first) {
          throw new Error('File is required for upload');
        }
        if (!input.providerId) {
          throw new Error('Provider ID is required for upload');
        }
        const metadata = resolveDocumentUploadMetadata(first, input.values);
        const formData = new FormData();
        // The name sent becomes uploaded_file_name (VARCHAR(64)); the parts keep their order,
        // which is the page order of a combined packet.
        if (combined) {
          combined.forEach((file) => formData.append('files', file, trimFileName(file.name)));
        } else {
          formData.append('file', first, trimFileName(first.name));
        }
        formData.append('providerId', `${input.providerId}`);
        formData.append('docCategory', metadata.docCategory);
        formData.append('docDesc', metadata.docDesc);
        formData.append('docName', metadata.docName);
        formData.append('docType', metadata.docType);

        // Use apiAxiosV1 directly to avoid interceptor Content-Type override
        return await apiAxiosV1.post<DocumentReference>(
          combined ? `/${ROUTE_NAME}/combine` : `/${ROUTE_NAME}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
    ),
    updateDocument: fromPromise(
      async ({ input }: { input: { id: number; values: DocumentInfoValues } }) => {
        const { id, values } = input;
        const { docName, docCategory, docDesc, docType } = values;
        return await patchData<DocumentReference>(ROUTE_NAME, {
          pathParams: { id: id?.toString() || '' },
          data: {
            docName,
            docCategory: docCategory?.value || undefined,
            docDesc,
            docType: docType.value,
          },
        });
      }
    ),
    deleteDocument: fromPromise(async ({ input }: { input: { id: number } }) => {
      return await deleteData(ROUTE_NAME, { pathParams: { id: input.id?.toString() || '' } });
    }),
    downloadDocument: fromPromise(async ({ input }: { input: { id: number } }) => {
      return await apiAxiosV1.get<Blob>(
        setPathParams(DOCUMENT_DOWNLOAD_ROUTES.get, { id: input.id.toString() }),
        { responseType: 'blob' }
      );
    }),
    aiParseDocument: fromPromise(
      async ({ input }: { input: { id: number; workflowType: ParseWorkflowType | null } }) => {
        return await patchData<DocumentParseResp>('aiJobs', {
          pathParams: { id: input.id.toString() },
          // The API defaults to cv-parse, so packets and certificates must say what they are.
          params: input.workflowType ? { workflow_type: input.workflowType } : undefined,
        });
      }
    ),
  },
  actions: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    handleServiceError: handleServiceError as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    assignDocumentsError: createErrorAssigner(documentsErrorMap) as any,
    addPlaceholderAiJob: assign(({ context }) => {
      if (!context.parsingDocumentId) {
        return {};
      }

      // Create a placeholder AI job to show parsing is in progress
      const placeholderJob = {
        id: `placeholder-${context.parsingDocumentId}-${Date.now()}`,
        document_id: context.parsingDocumentId,
        status: 'pending' as const,
        job_status: 'PENDING' as const,
        progress: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Update the document references with the placeholder
      const updatedDocumentReferences = context.documentReferences.map((doc) =>
        doc.id === context.parsingDocumentId
          ? { ...doc, aiJobs: [...doc.aiJobs, placeholderJob] }
          : doc
      );

      return {
        documentReferences: updatedDocumentReferences,
      };
    }),
    updateAiJobFromWebSocket: assign(({ context, event, self }) => {
      const e = event as { type: 'ON_JOB_UPDATE'; payload: WSAIParseJobData | WSAiJobUpdateData };
      const { payload } = e;

      // Handle both old and new formats
      const isNewFormat = 'jobType' in payload;
      const status = isNewFormat
        ? (payload.status.toLowerCase() as 'pending' | 'processing' | 'completed' | 'failed')
        : payload.status;

      const updatedJob: AIParseJob = {
        id: payload.jobId,
        document_id: payload.documentId,
        status: status,
        job_status: payload.status.toUpperCase() as
          | 'PENDING'
          | 'STARTED'
          | 'PROCESSING'
          | 'COMPLETED'
          | 'FAILED',
        progress: payload.progress ?? 0,
        error: payload.error,
        result: isNewFormat ? undefined : (payload as WSAIParseJobData).result,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Update the document references with the updated AI job
      const updatedDocumentReferences = context.documentReferences.map((doc) => {
        if (doc.id === payload.documentId) {
          // Remove any placeholder jobs when we receive a real job update
          const nonPlaceholderJobs = doc.aiJobs.filter((job) => !isPlaceholderAiJob(job));

          // Find existing job or add new one
          const existingJobIndex = nonPlaceholderJobs.findIndex(
            (job) => String(job.id) === String(payload.jobId)
          );
          const updatedAiJobs =
            existingJobIndex >= 0
              ? [
                  ...nonPlaceholderJobs.slice(0, existingJobIndex),
                  updatedJob,
                  ...nonPlaceholderJobs.slice(existingJobIndex + 1),
                ]
              : [...nonPlaceholderJobs, updatedJob];

          return { ...doc, aiJobs: updatedAiJobs };
        }
        return doc;
      });

      // Trigger refetch on completion to ensure we have final state
      if (status === 'completed' || status === 'failed') {
        // Refetch after a short delay to allow server to finalize
        setTimeout(() => {
          if (context.providerId) {
            self.send({
              type: 'GET_DOCUMENTS',
              providerId: context.providerId,
              forceRefresh: true,
            });
          }
        }, 500);
      }

      return {
        documentReferences: updatedDocumentReferences,
      };
    }),
    triggerBrowserDownload: ({ context, event }) => {
      const e = event as { output?: { data: Blob; headers?: unknown } };
      const blob = e.output?.data;
      if (!blob) return;

      // Look up docName from documentReferences
      const doc = context.documentReferences.find((d) => d.id === context.downloadingDocumentId);
      const filename = buildDownloadFilename({
        fallbackName: doc?.docName,
        headers: e.output?.headers,
      });

      // Create object URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
    },
  },
}).createMachine({
  id: 'documentsService',
  initial: '$_IDLE',
  context: () => ({
    providerId: null,
    documentReferences: [],
    cacheTimestamp: 0,
    cacheTTL: CACHE_TTL,
    loading: false,
    error: null,
    pendingDeleteId: null,
    downloadingDocumentId: null,
    uploadingProviderId: null,
    uploadingFile: null,
    uploadingFiles: null,
    uploadingValues: null,
    parsingDocumentId: null,
    ownedUploadNotices: [],
    viewRef: null,
  }),
  // [MCV-1800] Handled in every state: the batch records a stored file the moment it settles,
  // and the notification can arrive while the service is busy with the next one.
  on: {
    ON_UPLOAD_NOTICE_OWNED: {
      actions: assign({
        ownedUploadNotices: ({ context, event }) => [
          ...liveOwnedUploadNotices(context.ownedUploadNotices),
          { id: event.documentId, expiresAt: Date.now() + OWNED_UPLOAD_NOTICE_TTL_MS },
        ],
      }),
    },
    ON_UPLOAD_NOTICE_CLAIMED: {
      actions: assign({
        ownedUploadNotices: ({ context, event }) => {
          const live = liveOwnedUploadNotices(context.ownedUploadNotices);
          return event.documentId === undefined
            ? live.slice(1)
            : live.filter(({ id }) => id !== event.documentId);
        },
      }),
    },
  },
  states: {
    $_IDLE: {
      entry: [assign({ error: null })],
      on: {
        GET_DOCUMENTS: [
          // Use cache if valid and not forcing refresh
          {
            guard: ({ context, event }) =>
              !event.forceRefresh &&
              context.providerId === event.providerId &&
              context.documentReferences.length > 0 &&
              Date.now() - context.cacheTimestamp < context.cacheTTL,
            actions: () => undefined,
          },
          // Fetch fresh data
          {
            target: '$_FETCHING',
            actions: [
              assign({
                providerId: ({ event }) => event.providerId,
                viewRef: ({ event }) => (event as { viewRef?: AnyActorRef }).viewRef ?? null,
              }),
              () => undefined,
            ],
          },
        ],
        UPLOAD_DOCUMENT: {
          target: '$_UPLOADING',
          actions: [
            assign({
              viewRef: ({ event }) => (event as { viewRef: AnyActorRef }).viewRef,
              uploadingProviderId: ({ event }) => (event as { providerId: number }).providerId,
              uploadingFile: ({ event }) => (event as { file: File }).file,
              uploadingValues: ({ event }) =>
                (event as { values?: DocumentInfoValues }).values ?? null,
            }),
            () => undefined,
          ],
        },
        UPLOAD_COMBINED_DOCUMENT: {
          target: '$_UPLOADING',
          actions: assign({
            viewRef: ({ event }) => event.viewRef,
            uploadingProviderId: ({ event }) => event.providerId,
            uploadingFiles: ({ event }) => event.files,
            uploadingValues: ({ event }) => event.values ?? null,
          }),
        },
        UPDATE_DOCUMENT: {
          target: '$_UPDATING',
          actions: () => undefined,
        },
        AI_PARSE_DOCUMENT: {
          target: '$_AI_PARSING',
          actions: [assign({ parsingDocumentId: ({ event }) => event.id })],
        },
        ON_JOB_UPDATE: {
          actions: 'updateAiJobFromWebSocket',
        },
        ON_NOTIFICATION: {
          actions: () => undefined,
        },
        DELETE_DOCUMENT: {
          target: '$_DELETING_CONFIRMATION',
          actions: [assign({ pendingDeleteId: ({ event }) => event.id }), () => undefined],
        },
        DOWNLOAD_DOCUMENT: {
          target: '$_DOWNLOADING',
          actions: [assign({ downloadingDocumentId: ({ event }) => event.id }), () => undefined],
        },
        CLEAR_ERROR: {
          actions: assign({ error: null }),
        },
      },
    },
    $_FETCHING: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'getDocuments',
        input: ({ context }) => ({ providerId: context.providerId ?? 0 }),
        onDone: {
          target: '$_IDLE',
          actions: [
            () => undefined,
            assign({
              documentReferences: ({ event }) => event.output,
              loading: () => false,
              cacheTimestamp: () => Date.now(),
            }),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => console.error('[SERVICE] Fetch failed:', event.error),
            'handleServiceError',
            'assignDocumentsError',
            assign({ loading: () => false }),
          ],
        },
      },
    },
    $_UPLOADING: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'uploadDocument',
        input: ({ context }) => {
          return {
            providerId: context.uploadingProviderId,
            file: context.uploadingFile,
            files: context.uploadingFiles,
            values: context.uploadingValues,
          };
        },
        onDone: {
          target: '$_IDLE',
          actions: [
            () => undefined,
            assign({
              documentReferences: ({ context, event }) => [
                ...context.documentReferences,
                { ...event.output.data, aiJobs: [] },
              ],
              loading: () => false,
              cacheTimestamp: () => Date.now(),
              uploadingProviderId: () => null,
              uploadingFile: () => null,
              uploadingFiles: () => null,
              uploadingValues: () => null,
              // viewRef: () => null,
            }),
            // Send event to view machine using viewRef stored in context
            ({ context, event }) => {
              if (context.viewRef) {
                context.viewRef.send({
                  type: 'ON_DOCUMENT_UPLOADED',
                  documentReference: event.output.data,
                });
              }
            },
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => console.error('[SERVICE] Upload failed:', event.error),
            // No service-level alert: the viewRef owns the message, and a 415 carries the API's
            // readable reason (getUploadErrorAlert). An alert here too showed every failed
            // upload twice.
            'assignDocumentsError',
            assign({
              loading: () => false,
              uploadingProviderId: () => null,
              uploadingFile: () => null,
              uploadingFiles: () => null,
              uploadingValues: () => null,
              // viewRef: () => null,
            }),
            // Send error event to view machine using viewRef stored in context
            ({ context, event }) => {
              if (context.viewRef) {
                context.viewRef.send({
                  type: 'ON_DOCUMENT_UPLOAD_ERROR',
                  error: event.error,
                });
              }
            },
          ],
        },
      },
      on: {
        // Documents of an upload batch parse on upload, so their jobs report while a later
        // file is still uploading.
        ON_JOB_UPDATE: {
          actions: 'updateAiJobFromWebSocket',
        },
      },
    },
    $_UPDATING: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'updateDocument',
        input: ({ event }) => {
          const e = event as { type: string; id: number; values: DocumentInfoValues };
          return { id: e.id, values: e.values };
        },
        onDone: {
          target: '$_FETCHING',
          actions: [
            () => undefined,
            // Refresh the list to get updated data
            assign({
              providerId: ({ context }) => context.providerId,
            }),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => console.error('[SERVICE] Update failed:', event.error),
            'handleServiceError',
            'assignDocumentsError',
            assign({ loading: () => false }),
          ],
        },
      },
    },
    $_DELETING_CONFIRMATION: {
      entry: ({ self, system }) => {
        // Access confirmation dialog from system
        const confirmationView = system.get('confirmationViewState');
        if (confirmationView) {
          confirmationView.send({
            type: 'ON_SHOW_CONFIRMATION',
            payload: {
              title: 'Delete Document',
              message:
                'Are you sure you want to delete this document? This action cannot be undone.',
              confirmLabel: 'Delete',
              onConfirm: () => self.send({ type: 'CONFIRM_DELETE' }),
              onCancel: () => self.send({ type: 'CANCEL_DELETE' }),
            },
          });
        }
      },
      on: {
        CONFIRM_DELETE: {
          target: '$_DELETING',
          actions: () => undefined,
        },
        CANCEL_DELETE: {
          target: '$_IDLE',
          actions: [assign({ pendingDeleteId: null }), () => undefined],
        },
      },
    },
    $_DELETING: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'deleteDocument',
        input: ({ context }) => ({ id: context.pendingDeleteId ?? 0 }),
        onDone: {
          target: '$_FETCHING',
          actions: [
            () => undefined,
            assign({
              providerId: ({ context }) => context.providerId,
              pendingDeleteId: null,
            }),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => console.error('[SERVICE] Delete failed:', event.error),
            'handleServiceError',
            'assignDocumentsError',
            assign({ loading: () => false, pendingDeleteId: null }),
          ],
        },
      },
    },
    $_DOWNLOADING: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'downloadDocument',
        input: ({ event }) => {
          const e = event as { type: string; id: number };
          return { id: e.id };
        },
        onDone: {
          target: '$_IDLE',
          actions: [() => undefined, 'triggerBrowserDownload', assign({ loading: () => false })],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => console.error('[SERVICE] Download failed:', event.error),
            'handleServiceError',
            'assignDocumentsError',
            assign({ loading: () => false }),
          ],
        },
      },
    },
    $_AI_PARSING: {
      entry: [assign({ loading: true, error: null }), 'addPlaceholderAiJob'],
      invoke: {
        src: 'aiParseDocument',
        input: ({ context }) => {
          const doc = context.documentReferences.find(({ id }) => id === context.parsingDocumentId);
          return {
            id: context.parsingDocumentId ?? 0,
            workflowType: doc ? resolveParseWorkflow(doc) : null,
          };
        },
        onDone: {
          target: '$_IDLE',
          actions: [() => undefined, assign({ loading: () => false, parsingDocumentId: null })],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => console.error('[SERVICE] AI Parsing failed:', event.error),
            'handleServiceError',
            'assignDocumentsError',
            assign({ loading: () => false, parsingDocumentId: null }),
          ],
        },
      },
      on: {
        ON_JOB_UPDATE: {
          actions: 'updateAiJobFromWebSocket',
        },
        ON_NOTIFICATION: {
          actions: () => undefined,
        },
      },
    },
  },
});
